import { FilterOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import React, { FC, useCallback, useMemo } from "react";
import { Product } from "tm-shared/types";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { AppSelect } from "@/shared/forms/AppSelect";
import { patchProductsSelectedFilters } from "@/store/slices/ui";

export interface DropdownFiltersProps {
    products: Record<string, Product>;
    resetKey?: never;
}

const DropdownFilters: FC<DropdownFiltersProps> = ({ products }) => {
    const dispatch = useAppDispatch();
    const selectedFilters = useAppSelector(
        (state) => state.ui.productsFilter.selectedFilters,
    );

    const staticOptions = useMemo(() => {
        const groupSet = new Set<string>();
        const detailSet = new Set<string>();
        Object.values(products).forEach((p) => {
            const g = p.params.fin_group?.value;
            if (g !== undefined) {
                if (Array.isArray(g)) g.forEach((v) => groupSet.add(String(v)));
                else groupSet.add(String(g));
            }
            const d = p.params.fin_group_detail?.value;
            if (d !== undefined) {
                if (Array.isArray(d))
                    d.forEach((v) => detailSet.add(String(v)));
                else detailSet.add(String(d));
            }
        });
        return {
            fin_group: Array.from(groupSet).sort((a, b) =>
                a.localeCompare(b, "ua", { sensitivity: "base" }),
            ),
            fin_group_detail: Array.from(detailSet).sort((a, b) =>
                a.localeCompare(b, "ua", { sensitivity: "base" }),
            ),
        };
    }, [products]);

    const onGroupChange = useCallback(
        async (value: string) => {
            dispatch(patchProductsSelectedFilters({}));
            dispatch(
                patchProductsSelectedFilters({
                    fin_group: value ? [value] : [],
                }),
            );
            dispatch(patchProductsSelectedFilters({ fin_group_detail: [] }));
        },
        [dispatch],
    );

    const onDetailChange = useCallback(
        async (value: string) => {
            dispatch(patchProductsSelectedFilters({}));
            dispatch(
                patchProductsSelectedFilters({
                    fin_group_detail: value ? [value] : [],
                }),
            );
            dispatch(patchProductsSelectedFilters({ fin_group: [] }));
        },
        [dispatch],
    );

    const finGroupOptions = staticOptions.fin_group.map((finGroup) => ({
        label: finGroup,
        value: finGroup,
    }));

    const finGroupDetailOptions = staticOptions.fin_group_detail.map(
        (finGroupDetail) => ({
            label: finGroupDetail,
            value: finGroupDetail,
        }),
    );

    return (
        <Flex gap={8}>
            <AppSelect
                onChange={onGroupChange}
                value={selectedFilters.fin_group || []}
                listHeight={400}
                allowClear
                size="large"
                style={{ flex: 1 }}
                popupMatchSelectWidth={false}
                options={finGroupOptions}
                styles={{
                    popup: {
                        root: {
                            width: "92%",
                        },
                    },
                }}
                placeholder={
                    <>
                        <FilterOutlined /> &nbsp; Група
                    </>
                }
            />

            <AppSelect
                value={selectedFilters.fin_group_detail || []}
                onChange={onDetailChange}
                listHeight={400}
                allowClear
                size="large"
                style={{ flex: 1 }}
                popupMatchSelectWidth={false}
                styles={{
                    popup: {
                        root: {
                            width: "92%",
                        },
                    },
                }}
                placeholder={
                    <>
                        <FilterOutlined /> &nbsp; Детальна група
                    </>
                }
                options={finGroupDetailOptions}
            />
        </Flex>
    );
};

export default DropdownFilters;
